import React from 'react';
import {
  BooleanInput,
  TextInput,
  Create,
  SimpleForm,
  FormDataConsumer,
  useGetOne,
  useGetList,
  minValue,
  required,
} from 'react-admin';
import EnumSelectInput from 'components/Enum/EnumSelectInput';
import PositionInput from 'components/PositionInput';
import SpecialtyInput from 'components/SpecialtyInput';
import LocationInput from 'components/LocationInput';
import MoneyInput from 'components/MoneyInput';

const useRateValidation = (locationId, positionIds, specialtyIds) => {
  const { data: rateSettings, isLoading } = useGetList(
    'LocationPositionRateSetting',
    {
      filter: {
        max_only: true,
        include_parents: true,
        location_id: locationId,
        search: {
          position_ids: positionIds,
          specialty_ids: specialtyIds,
        },
      },
    },
    { enabled: !!(locationId && positionIds?.length) },
  );

  const rateSetting = rateSettings?.[0];
  const minRate = rateSetting?.settings?.ten99?.charge_rate_cents || 0;
  const suggestedRate = minRate ? Math.floor(minRate * 1.12) : 0;

  const validateRate = (value) => {
    if (!minRate) {
      return undefined;
    }
    if (!value || value < minRate) {
      return `Rate must be at least $${(minRate / 100).toFixed(2)} (suggested: $${(
        suggestedRate / 100
      ).toFixed(2)})`;
    }
    return undefined;
  };

  return {
    minRate,
    suggestedRate,
    validateRate,
    isLoading,
  };
};

const LocationRateField = ({ locationId, positionIds, specialtyIds }) => {
  const { data: location, isLoading: locationLoading } = useGetOne('Location', { id: locationId });
  const { data: company, isLoading: companyLoading } = useGetOne(
    'Company',
    { id: location?.company_id },
    { enabled: !!location?.company_id },
  );

  const {
    minRate,
    suggestedRate,
    validateRate,
    isLoading: rateValidationLoading,
  } = useRateValidation(locationId, positionIds, specialtyIds);

  if (locationLoading || companyLoading || rateValidationLoading) {
    return null;
  }

  if (!location || !company) {
    return null;
  }

  if (company.enable_template_rate) {
    const helperText = minRate
      ? `Minimum: $${(minRate / 100).toFixed(2)}, Suggested: $${(suggestedRate / 100).toFixed(2)}`
      : undefined;

    return (
      <MoneyInput
        source="charge_base_hourly_rate_cents"
        label="Rate"
        validate={[minValue(1, 'Rate is required'), validateRate]}
        helperText={helperText}
      />
    );
  }

  return null;
};

const JobTemplateCreate = () => (
  <Create redirect="show">
    <SimpleForm>
      <LocationInput source="location_id" />
      <EnumSelectInput source="kind" />
      <PositionInput multiple filter={{ active: true }} />
      <SpecialtyInput multiple positionSource="position_ids" optionText="name" />
      <EnumSelectInput source="patient_population" />
      <BooleanInput source="covid_care" />
      <TextInput multiline fullWidth source="detail" />
      <TextInput source="name" validate={[required()]} />
      <FormDataConsumer>
        {({ formData }) => {
          const locationId = formData.location_id;
          const positionIds = formData.position_ids;
          const specialtyIds = formData.specialty_ids;

          if (!locationId) {
            return null;
          }

          return (
            <LocationRateField
              locationId={locationId}
              positionIds={positionIds}
              specialtyIds={specialtyIds}
            />
          );
        }}
      </FormDataConsumer>
    </SimpleForm>
  </Create>
);

export default JobTemplateCreate;
